import React from 'react';
import { CheckCircle, AlertTriangle, Target, Calculator } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MealTypeConfig } from './MealTypesIntegrated';
import { validateMealDistributionAgainstDCT, DCTValidationResult } from '@/utils/meal-dct-validation';

interface MealKcalComparisonStatusProps {
  mealTypes: MealTypeConfig[];
  dctTarget: number | null | undefined;
  className?: string;
}

interface ComparisonResult extends DCTValidationResult {
  color: 'green' | 'red' | 'yellow' | 'gray';
  icon: React.ReactNode;
}

export const MealKcalComparisonStatus: React.FC<MealKcalComparisonStatusProps> = ({
  mealTypes,
  dctTarget,
  className = ''
}) => {
  
  const calculateComparison = (): ComparisonResult => {
    const validationResult = validateMealDistributionAgainstDCT(mealTypes, dctTarget);
    
    // Map validation result to UI properties
    let color: 'green' | 'red' | 'yellow' | 'gray';
    let icon: React.ReactNode;
    
    switch (validationResult.status) {
      case 'within_target':
        color = 'green';
        icon = <CheckCircle className="w-4 h-4" />;
        break;
      case 'over_target':
      case 'under_target':
        color = validationResult.canProceed ? 'yellow' : 'red';
        icon = <AlertTriangle className="w-4 h-4" />;
        break;
      case 'no_dct':
        color = 'gray';
        icon = <Target className="w-4 h-4" />;
        break;
      case 'no_meals':
        color = 'gray';
        icon = <Calculator className="w-4 h-4" />;
        break;
      default:
        color = 'gray';
        icon = <Target className="w-4 h-4" />;
    }
    
    return {
      ...validationResult,
      color,
      icon
    };
  };

  const result = calculateComparison();

  // Don't render comparison if no meal types, or if no kcal values are specified (Edamam scenario)
  if (mealTypes.length === 0 || result.status === 'no_dct' || result.status === 'no_meals') {
    return null;
  }

  const getStatusBadgeVariant = (color: string) => {
    switch (color) {
      case 'green': return 'default';
      case 'red': return 'destructive';
      case 'yellow': return 'secondary';
      default: return 'outline';
    }
  };

  const getCardBorderColor = (color: string) => {
    switch (color) {
      case 'green': return 'border-green-200 bg-green-50/50';
      case 'red': return 'border-red-200 bg-red-50/50';
      case 'yellow': return 'border-yellow-200 bg-yellow-50/50';
      default: return 'border-gray-200 bg-gray-50/50';
    }
  };

  const getTextColor = (color: string) => {
    switch (color) {
      case 'green': return 'text-green-700';
      case 'red': return 'text-red-700';
      case 'yellow': return 'text-yellow-700';
      default: return 'text-gray-700';
    }
  };

  return (
    <Card className={`${className} ${getCardBorderColor(result.color)} border-2`}>
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Header with status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={getTextColor(result.color)}>
                {result.icon}
              </div>
              <h4 className="font-medium text-sm">Meal Calories vs Daily Goals</h4>
            </div>
            <Badge 
              variant={getStatusBadgeVariant(result.color)}
              className="text-xs"
            >
              {result.status === 'within_target' && '✓ Within Target'}
              {result.status === 'over_target' && `+${result.difference} kcal`}
              {result.status === 'under_target' && `${result.difference} kcal`}
            </Badge>
          </div>

          {/* Comparison details */}
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <p className="text-muted-foreground text-xs">Meal Total</p>
              <p className="font-medium">{result.totalMealKcals} kcal</p>
            </div>
            <div className="text-center">
              <p className="text-muted-foreground text-xs">Daily Goals</p>
              <p className="font-medium">{result.dctTarget} kcal</p>
            </div>
            <div className="text-center">
              <p className="text-muted-foreground text-xs">Difference</p>
              <p className={`font-medium ${getTextColor(result.color)}`}>
                {result.percentageDifference.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Status message */}
          <div className={`p-3 rounded-lg border ${getCardBorderColor(result.color)}`}>
            <div className="flex items-start gap-2">
              <div className={`mt-0.5 ${getTextColor(result.color)}`}>
                {result.icon}
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${getTextColor(result.color)}`}>
                  {result.status === 'within_target' && '✓ Within target'}
                  {(result.status === 'over_target' || result.status === 'under_target') && result.message}
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
